import {
  Component,
  OnInit,
  Input,
  Inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActionModel } from '../action.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActionNoteModel } from '../action-note.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { NgForm } from '@angular/forms';
import { UtilsService } from 'src/app/shared/utils.service';
import { RBACService } from 'src/app/shared/rbac.service';
import { AppService } from 'src/app/app.service';

@Component({
  selector: 'app-actions-notes-edit',
  templateUrl: './actions-notes-edit.component.html',
  styleUrls: ['./actions-notes-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ActionsNotesEditComponent implements OnInit {
  action: ActionModel;

  note: ActionNoteModel = new ActionNoteModel();

  inProgress = false;

  // RBAC permission flag. Resolved once in ngOnInit() so the template does not
  // re-evaluate the permission check on every change detection cycle.
  canEdit: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ActionsNotesEditComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { action: ActionModel; noteId: number },
    private api: ApiRequestService,
    private app: AppService,
    private utils: UtilsService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    this.action = this.data.action;

    // Resolve RBAC permission flags. A user writing notes on their own task does so through the core
    // profile permission, because this dialog is shared with the personal My Tasks view where the
    // user may hold no feature permissions at all. Matches the API and the notes list component.
    this.canEdit =
      ActionModel.isOwnedBy(this.action, this.app.user?.id) ||
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TASKS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      );

    if (this.action.id && this.data.noteId) {
      this.api
        .makeRequest(
          'get',
          `v1/actions/${this.action.id}/notes/${this.data.noteId}`,
          {},
          {},
          {}
        )
        .then((response: any) => {
          this.note = response.data;
        });
    }
  }

  onSubmit(form: NgForm) {
    // Uses the flag resolved in ngOnInit() so this guard cannot disagree with the Save button.
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit task notes.'
      );
      return;
    }

    if (!form.valid) {
      this.utils.showModal(
        'Validation Error',
        'Please make sure you enter all required fields.'
      );
      return;
    }

    if (this.inProgress) {
      return;
    }

    this.inProgress = true;

    if (this.data.noteId) {
      this.api
        .makeRequest(
          'put',
          `v1/actions/${this.action.id}/notes/${this.data.noteId}`,
          this.note,
          {},
          {}
        )
        .then((resposne: any) => {
          this.utils.showToast('Note Updated.');
          this.inProgress = false;
        })
        .catch((_error: any) => (this.inProgress = false));
    } else {
      this.api
        .makeRequest(
          'post',
          `v1/actions/${this.action.id}/notes`,
          this.note,
          {},
          {}
        )
        .then((resposne: any) => {
          this.utils.showToast('Note Created.');
          this.dialogRef.close();
          this.inProgress = false;
        })
        .catch((_error: any) => (this.inProgress = false));
    }
  }

  onDelete() {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete task notes.'
      );
      return;
    }

    if (!this.data.noteId) {
      return;
    }

    this.utils.showModal(
      'Delete Note',
      'Are you sure you want to delete the note?',
      () => {
        this.api
          .makeRequest(
            'delete',
            `v1/actions/${this.action.id}/notes/${this.data.noteId}`,
            {},
            {},
            {}
          )
          .then((resposne: any) => {
            this.utils.showToast('Note Deleted.');
            this.dialogRef.close();
          });
      }
    );
  }

  // Expose RBACService to template.
  protected readonly RBACService = RBACService;
}
